<template>
    <oss-dialog
        v-model="isOpen"
        title="Choisissez votre mode de paiement"
        :previous-display="wizardStep !== 0"
        @previous="back"
        :actions="dialogActions"
        prefixDataCy="ConfigurePaymentModeDialog"
        min-width="40vw"
    >
        <v-window v-model="wizardStep">
            <!-- Step 1 : Select payement mode -->
            <v-window-item :value="0">
                <v-list>
                    <v-list-item
                        v-for="option in paymentOptions"
                        :key="option.value"
                        @click="selectPaymentMethod(option.value)"
                        ripple
                        class="oss-list-item-xl mb-2 py-3"
                        :data-cy="`PaymentModeWizard-paymentModeOption-${option.value}`"
                    >
                        <v-list-item-title>{{ option.label }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-window-item>

            <!-- Step 2 : Enter information according to choice in step 1 -->
            <v-window-item :value="1">
                <v-text-field-filled
                    v-if="selectedPaymentMethod === 'check'"
                    label="Information complémentaire"
                    v-model="paymentModeDetails"
                    data-cy="LegalForm-paymentMode-details-check"
                />
                <v-text-field-filled
                    v-if="selectedPaymentMethod === 'direct_debit'"
                    label="Information complémentaire"
                    v-model="paymentModeDetails"
                    data-cy="LegalForm-paymentMode-details-debit"
                />
                <v-select-filled
                    v-if="selectedPaymentMethod === 'bank_transfer'"
                    label="Sélectionnez un compte bancaire"
                    v-model="paymentModeDetails"
                    :items="formattedBankAccounts"
                    item-title="name"
                    item-value="iban"
                    data-cy="PaymentModeWizard-bankTransferDetails"
                >
                    <template v-slot:item="{ props, item }">
                        <v-list-item v-bind="props" :subtitle="item.raw.iban"></v-list-item>
                    </template>
                </v-select-filled>
                <v-textarea-filled
                    v-if="selectedPaymentMethod === 'other'"
                    label="Instructions personnalisées"
                    v-model="paymentModeDetails"
                    data-cy="LegalForm-paymentMode-details-other"
                />
            </v-window-item>
        </v-window>
    </oss-dialog>
</template>

<script lang="ts">
import { Component, Vue, Prop, toNative, Watch, Model } from "vue-facing-decorator";
import { Apollo } from "@/decorators";
import BANK_ACCOUNTS from "../../graphql/bank/BankAccountsWithBTCount.gql";
import { DialogAction } from "@/services/dialogAction";
import { BankAccount } from "../../../../types";

@Component({
    emits: ["update:value", "paymentModeSelected"],
})
export class PaymentModeWizard extends Vue {
    @Model() value: boolean;
    @Prop({ required: true }) companyId!: string;

    wizardStep: number = 0;
    selectedPaymentMethod: string | null = null;
    paymentModeDetails: string = "";
    dialogActions = {
        action: new DialogAction(),
    };
    paymentOptions = [
        { label: "Chèque", value: "check" },
        { label: "Prélèvement", value: "direct_debit" },
        { label: "Virement bancaire", value: "bank_transfer" },
        { label: "Autre", value: "other" },
    ];

    // Récupération des tous les comptes bancaires du cabinet
    @Apollo(
        {
            query: BANK_ACCOUNTS,
            variables() {
                return {
                    companyId: this.companyId,
                    type: "BANK",
                };
            },
            skip() {
                return !this.companyId;
            },
        },
        (data) => (data && data.bankAccounts ? data.bankAccounts : []),
    )
    availableBankAccounts: BankAccount[] = [];

    get isOpen() {
        return this.value;
    }

    set isOpen(event: boolean) {
        this.value = event;
    }

    get formattedBankAccounts() {
        return this.availableBankAccounts
            .filter((account) => account.iban)
            .map((account) => ({
                name: account.name,
                iban: account.iban,
            }));
    }

    /** Watcher pour gérer les actions du wizard */
    @Watch("wizardStep")
    onWizardStepChanged() {
        switch (this.wizardStep) {
            case 0:
                this.dialogActions.action.display = false;
                break;
            case 1:
                this.dialogActions.action.display = true;
                this.dialogActions.action.disabled = () => !this.paymentModeDetails;
                this.dialogActions.action.title = () => "Valider";
                this.dialogActions.action.function = this.validatePaymentMode;
                break;
        }
    }

    beforeMount() {
        this.dialogActions.action.display = false;
    }

    // Select a payment method and proceed to the next step
    selectPaymentMethod(method: string) {
        this.selectedPaymentMethod = method;
        switch (method) {
            case "check":
                this.paymentModeDetails = "Payer à l'ordre de ...";
                break;
            case "direct_debit":
                this.paymentModeDetails = "Échéance le …";
                break;
            case "bank_transfer":
                this.paymentModeDetails = "";
                break;
            case "other":
                this.paymentModeDetails = "";
                break;
        }

        this.wizardStep = 1;
    }

    back() {
        if (this.wizardStep > 0) {
            this.wizardStep--;
        }
    }
    // Validate the payment mode and emit the selected payment mode
    validatePaymentMode() {
        let paymentText = "";
        switch (this.selectedPaymentMethod) {
            case "check":
                paymentText = `Modalité de paiement : Chèque\n${this.paymentModeDetails}.`;
                break;
            case "direct_debit":
                paymentText = `Modalité de paiement : Prélèvement\n${this.paymentModeDetails}`;
                break;
            case "bank_transfer":
                paymentText = `Modalité de paiement : Virement vers le compte\n${this.paymentModeDetails}`;
                break;
            case "other":
                paymentText = this.paymentModeDetails;
                break;
        }
        this.$emit("paymentModeSelected", paymentText);
        this.isOpen = false;
    }
}

export default toNative(PaymentModeWizard);
</script>
